import { CarouselCard, Page } from '@/components'
import IconMapper, { Icon } from '@/components/icons/IconMapper'
import { PUBLIC_URL } from '@/config'
import { useAppSelector } from '@/redux/hooks/hooks'
import { color } from '@/style/variables'
import { Button, Card, config, H1, H2, H3, H5, Image, Paragraph, Spinner, styled, View, XStack, YStack } from '@red-ui/components'
import { getString } from '@vrw/data'
import { getBalance } from '@vrw/data/src/redux/balance/balance.selectors'
import { getProfileData } from '@vrw/data/src/redux/profile/selectors'
import { formatPoints } from '@vrw/data/src/utils/formatters'
import React, { useCallback, useState } from 'react'
import AliceCarousel from 'react-alice-carousel'
import { SearchSideSheet } from '../components/SearchSideSheet'
import { getTrackingId } from '../helpers/getTrackingId'
import { useRedNavigate } from '@/dataImplementation/useRedNavigate'
import { PATHS } from '@/router/paths'
import { useVaaAuth } from '../hooks/useVaaAuth'
import { EventCallbackFunction, EventType } from '@azure/msal-browser'

export function LandingPage() {
  const profile = useAppSelector(getProfileData)
  const points = useAppSelector(getBalance)
  const navigate = useRedNavigate()
  const [cardHeight, setCardHeight] = useState(0)
  const { login, data, isLoading } = useVaaAuth({
    eventCallback: useCallback<EventCallbackFunction>(
      (message) => {
        if (message.eventType === EventType.LOGIN_SUCCESS) {
          navigate(PATHS.REDSKY_SEARCH_EDIT)
        }
      },
      [navigate]
    ),
  })
  const carouselItems = Array.from({ length: 3 }, (_, i) => i + 1)

  const handleSearchSideSheet = () => {
    if (data) navigate(PATHS.REDSKY_SEARCH_EDIT)
    else login()
  }

  const StyledCarouselButton = styled(Button, {
    borderRadius: 50,
    width: '$6',
    height: '$6',
    display: 'none',
    zIndex: 1,
    position: 'absolute',
    $gtTabletLandscape: {
      display: 'flex',
      top: cardHeight / 2 - 70,
    },
    disabledStyle: {
      display: 'none',
    },
    justifyContent: 'center',
    alignItems: 'center',
    padding: 0,
    cursor: 'pointer',
  })

  const StyledCarouselDots = styled(Button, {
    variants: {
      isActive: {
        true: {
          backgroundColor: '$red6',
          hoverStyle: {
            backgroundColor: '$red6',
          },
          pressStyle: {
            backgroundColor: '$red6',
          },
        },
      },
    },
    backgroundColor: '$white',
    borderRadius: '$6',
    height: '$3',
    width: '$3',
    borderColor: '$red6',
    marginHorizontal: '$2',
    hoverStyle: {
      backgroundColor: '$white',
    },
    pressStyle: {
      backgroundColor: '$white',
    },
  })

  return (
    <Page title={getString('redSkySearch.title')}>
      {data ? <SearchSideSheet /> : null}
      <XStack
        backgroundColor="$pink4"
        justifyContent="center"
        paddingHorizontal="$3"
        $mobile={{ paddingHorizontal: '$2' }}
        paddingVertical="$5">
        <YStack flex={1} gap="$5" maxWidth={config.media.desktop.maxWidth} zIndex="$0">
          <YStack alignItems="center" gap="$2">
            <H1 fontStyle="italic" fontWeight="600" textAlign="center" $mobile={{ fontSize: '$7' }}>
              {getString('redSkySearch.landing.title')}
            </H1>
            <Paragraph margin="$0" textAlign="center" $mobile={{ fontSize: '$6' }} style={{ whiteSpace: 'pre-line' }}>
              {getString('redSkySearch.landing.description')}
            </Paragraph>
          </YStack>
          <Card overflow="hidden">
            <Card.Header padding="$0" height={300} $gtMobile={{ height: 500 }}>
              <Image
                aria-label={getString('redSkySearch.landing.main.image.alt')}
                source={{ uri: `${PUBLIC_URL}/img/red-sky/landing-image.png` }}
                flex={1}
              />
            </Card.Header>
            <Card.Footer
              $gtMobile={{
                zIndex: '$1',
                left: '$7',
                top: '$7',
                position: 'absolute',
                minWidth: 350,
                width: '30%',
                borderRadius: '$3',
                paddingVertical: '$4',
              }}
              backgroundColor="$red6"
              flexDirection="column"
              padding="$3">
              <H3
                marginBottom="$4"
                $mobile={{ marginBottom: '$2', fontSize: '$5' }}
                $tabletLandscape={{ fontSize: '$7' }}
                color="$white"
                fontWeight="700">
                {getString('redSkySearch.landing.book.welcome').replace('{{name}}', profile?.firstName ?? '')}
              </H3>
              <YStack gap="$2" marginBottom="$10" $mobile={{ marginBottom: '$3', gap: '$1' }}>
                <H5 color="$white" $mobile={{ fontSize: '$4' }} fontWeight="600">
                  {getString('redSkySearch.landing.book.points').replace('{{points}}', formatPoints({ points: points.usable ?? 0 }))}
                </H5>
                <Paragraph color="$white" $mobile={{ fontSize: '$4' }}>
                  {getString('redSkySearch.landing.book.description')}
                </Paragraph>
              </YStack>
              <Button
                disabled={isLoading}
                iconAfter={isLoading ? <Spinner color="$red6" /> : null}
                id={getTrackingId(getString('redSkySearch.landing.book.cta'))}
                variant="secondary"
                padding="$3"
                onPress={handleSearchSideSheet}>
                {getString('redSkySearch.landing.book.cta')}
              </Button>
            </Card.Footer>
          </Card>
        </YStack>
      </XStack>
      <YStack $gtTabletLandscape={{ marginVertical: '$4' }} flex={1}>
        <H2
          textAlign="center"
          marginTop="$5"
          marginBottom="$1"
          fontWeight="600"
          $mobile={{ fontSize: '$6' }}
          $tabletPortrait={{ fontSize: '$8' }}
          $tabletLandscape={{ fontSize: '$8' }}>
          {getString('redSkySearch.onboardingCarousel.heading')}
        </H2>
        <View onLayout={(e) => setCardHeight(e.nativeEvent.layout.height)}>
          <AliceCarousel
            items={carouselItems.map((index) => {
              return (
                <CarouselCard key={index}>
                  <YStack flex={1} height="100%">
                    <Image
                      width="100%"
                      height={300}
                      $gtMobile={{ display: 'none' }}
                      display="flex"
                      aria-label={getString(`redSkySearch.onboardingCarousel.${index}.image.alt`)}
                      marginBottom="$2"
                      source={{ uri: `${PUBLIC_URL}/img/red-sky/onboarding-carousel-${index}.png` }}
                    />
                    <XStack flex={1}>
                      <YStack
                        justifyContent="center"
                        paddingHorizontal="$4"
                        height="100%"
                        $gtMobile={{ width: '50%', paddingTop: '$3' }}
                        width="100%">
                        <H2
                          fontWeight="600"
                          paddingBottom="$3"
                          $gtTabletLandscape={{ width: '75%' }}
                          $tabletPortrait={{ fontSize: '$8' }}
                          $tabletLandscape={{ fontSize: '$8' }}
                          $mobile={{ fontSize: '$6', lineHeight: '$6', textAlign: 'center' }}>
                          {getString(`redSkySearch.onboardingCarousel.${index}.heading`)}
                        </H2>
                        <Paragraph
                          paddingBottom="$4"
                          $gtTabletLandscape={{ width: '80%' }}
                          $tabletPortrait={{ fontSize: '$5' }}
                          $tabletLandscape={{ fontSize: '$5' }}
                          $mobile={{ fontSize: '$4', textAlign: 'center' }}>
                          {getString(`redSkySearch.onboardingCarousel.${index}.body`)}
                        </Paragraph>
                      </YStack>
                      <Image
                        width="50%"
                        $gtMobile={{ display: 'flex' }}
                        aria-label={getString(`redSkySearch.onboardingCarousel.${index}.image.alt`)}
                        source={{ uri: `${PUBLIC_URL}/img/red-sky/onboarding-carousel-${index}.png` }}
                      />
                    </XStack>
                  </YStack>
                </CarouselCard>
              )
            })}
            renderPrevButton={({ isDisabled }) => (
              <StyledCarouselButton
                left={28}
                disabled={isDisabled}
                icon={<IconMapper icon={Icon.ArrowLeft} color={color.white} size={25} />}
                aria-label={getString('actions.back')}
              />
            )}
            renderNextButton={({ isDisabled }) => (
              <StyledCarouselButton
                right={28}
                disabled={isDisabled}
                icon={<IconMapper icon={Icon.ArrowRight} color={color.white} size={25} />}
                aria-label={getString('actions.next')}
              />
            )}
            renderDotsItem={({ isActive }) => (
              <StyledCarouselDots isActive={isActive} aria-label={getString('redSkySearch.onboardingCarousel.dot')} />
            )}
          />
        </View>
      </YStack>
    </Page>
  )
}
